import { clampPage, pageCount, pageSizeOptions } from './paginate';

export interface PaginationProps {
  /** Rows after filtering, not the whole set — the control reports what is reachable from here. */
  totalItems: number;

  /** 1-based. The caller clamps it; see `clampPage`. */
  page: number;
  pageSize: number;

  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;

  /** Plural noun for the count line, e.g. "assessments". */
  itemNoun: string;
}

/**
 * Rows-per-page, a page stepper, and a count of what is being shown.
 *
 * The count line is not decoration: with a filter above the table and a page size below it, "3
 * assessments" on screen has at least three possible explanations, and saying "showing 1–10 of 47"
 * removes all of them.
 */
export function Pagination({
  totalItems,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  itemNoun,
}: PaginationProps) {
  const pages = pageCount(totalItems, pageSize);
  const current = clampPage(page, totalItems, pageSize);

  const first = totalItems === 0 ? 0 : (current - 1) * pageSize + 1;
  const last = Math.min(current * pageSize, totalItems);

  return (
    <div className="pagination">
      <label className="pagination__size">
        Rows per page
        <select
          className="control pagination__select"
          value={pageSize}
          onChange={(event) => onPageSizeChange(Number(event.target.value))}
        >
          {pageSizeOptions.map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </select>
      </label>

      <p className="pagination__count">
        {totalItems === 0 ? `No ${itemNoun}` : `Showing ${first}–${last} of ${totalItems} ${itemNoun}`}
      </p>

      <div className="pagination__pages">
        <button
          type="button"
          className="button button--quiet button--small"
          disabled={current <= 1}
          onClick={() => onPageChange(current - 1)}
        >
          ← Previous
        </button>

        {/* A page stepper rather than a numbered strip: 47 assessments is five pages, and at a
            hundred it would be one. Neither is a set anyone navigates by picking page 7. */}
        <span className="pagination__position">
          Page {current} of {pages}
        </span>

        <button
          type="button"
          className="button button--quiet button--small"
          disabled={current >= pages}
          onClick={() => onPageChange(current + 1)}
        >
          Next →
        </button>
      </div>
    </div>
  );
}
